/**
 * Écran de consent OAuth 2.1 (MCP-P1.4).
 *
 * Un client MCP (Claude Desktop, claude.ai...) redirige le navigateur ici avec
 * les query params standard OAuth :
 *   - client_id
 *   - redirect_uri
 *   - scope (séparé par espace)
 *   - code_challenge
 *   - code_challenge_method (S256)
 *   - state
 *   - response_type (= code)
 *
 * Flow :
 *  1. Si non connecté → redirige vers /login avec returnTo (la SPA revient ici
 *     après login).
 *  2. Charge le contexte via GET /oauth/authorize?... (le backend valide les
 *     paramètres et renvoie nom du client + scopes grantables).
 *  3. Affiche l'écran de consent avec descriptions humaines des scopes.
 *  4. À la décision (allow/deny) → POST /oauth/authorize/consent → reçoit
 *     `redirect_to` → window.location.href.
 */
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import {
  getOauthAuthorizeContext,
  getErrorMessage,
  listWorkspaces,
  submitOauthConsent,
  type OAuthAuthorizeContext,
} from '@/lib/api';

const SCOPE_LABELS: Record<string, { title: string; description: string; sensitive?: boolean }> = {
  'surveys:read': {
    title: 'Lire vos enquêtes',
    description: 'Liste, métadonnées, structure (blocs / sections / questions) — pas les réponses.',
  },
  'surveys:write': {
    title: 'Créer, modifier, publier vos enquêtes',
    description: 'Créer une enquête, modifier son titre, la publier, l’archiver.',
  },
  'definitions:write': {
    title: 'Modifier la structure des enquêtes',
    description: 'Ajouter / modifier / supprimer des blocs, sections et questions.',
  },
  'aggregates:read': {
    title: 'Lire les statistiques agrégées',
    description: 'Comptages, distributions, agrégats anonymisés (k≥5). Pas de réponses individuelles.',
  },
  'responses:read': {
    title: '⚠ Lire les réponses individuelles',
    description: 'Accès aux réponses brutes (identifiées). Désactivé tant que l’AIPD enquête n’a pas été signée avec mention du sous-traitant IA.',
    sensitive: true,
  },
};

export default function OAuthConsentPage(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // 1. Récupère le workspace courant (P1.4 — on suppose 1 seul workspace ; si
  //    plusieurs, on prend le premier sans demander). À étendre si besoin.
  const wsQuery = useQuery({ queryKey: ['workspaces'], queryFn: listWorkspaces });
  const workspaceId = wsQuery.data?.[0]?.id ?? null;
  const workspaceName = wsQuery.data?.[0]?.name ?? null;

  // 2. Charge le contexte OAuth.
  const ctxQuery = useQuery<OAuthAuthorizeContext>({
    queryKey: ['oauth-authorize', params.toString()],
    queryFn: () => getOauthAuthorizeContext(workspaceId!, params),
    enabled: !!workspaceId,
  });

  const [busy, setBusy] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  async function decide(decision: 'allow' | 'deny'): Promise<void> {
    if (!ctxQuery.data || !workspaceId) return;
    setBusy(true);
    setErrMsg('');
    try {
      const res = await submitOauthConsent(workspaceId, {
        client_id: ctxQuery.data.client_id,
        redirect_uri: ctxQuery.data.redirect_uri,
        state: ctxQuery.data.state,
        code_challenge: ctxQuery.data.code_challenge,
        code_challenge_method: ctxQuery.data.code_challenge_method,
        scope: ctxQuery.data.grantable_scopes.join(' '),
        decision,
      });
      // Le backend a déjà construit l'URL avec code/state ou error. On suit.
      window.location.href = res.redirect_to;
    } catch (err) {
      setErrMsg(getErrorMessage(err));
      setBusy(false);
    }
  }

  if (wsQuery.isLoading || ctxQuery.isLoading) {
    return <ConsentShell><p className="text-text-muted">Chargement…</p></ConsentShell>;
  }
  if (!workspaceId) {
    return (
      <ConsentShell>
        <h1 className="text-xl font-semibold">Workspace introuvable</h1>
        <p className="text-text-muted mt-2">
          Vous devez avoir au moins un workspace actif pour autoriser une application MCP.
        </p>
        <div className="mt-4">
          <button className="btn-primary" onClick={() => navigate('/workspaces')}>Mes workspaces</button>
        </div>
      </ConsentShell>
    );
  }
  if (ctxQuery.isError || !ctxQuery.data) {
    return (
      <ConsentShell>
        <h1 className="text-xl font-semibold">Requête OAuth invalide</h1>
        <p className="text-text-muted mt-2">{getErrorMessage(ctxQuery.error)}</p>
      </ConsentShell>
    );
  }

  const ctx = ctxQuery.data;

  return (
    <ConsentShell>
      <header className="space-y-2">
        <p className="text-xs uppercase tracking-[0.14em] text-text-muted font-semibold">
          Connecteur IA
        </p>
        <h1 className="text-2xl font-semibold">
          Autoriser <span className="text-primary">{ctx.client_name}</span> ?
        </h1>
        <p className="text-text-muted">
          Cette application demande l’accès au workspace{' '}
          <strong className="text-text">{workspaceName ?? workspaceId}</strong>{' '}
          pour effectuer des actions en votre nom.
        </p>
      </header>

      <section className="space-y-2">
        <p className="text-sm font-semibold text-text">Permissions demandées :</p>
        <ul className="space-y-2">
          {ctx.grantable_scopes.map((scope) => {
            const info = SCOPE_LABELS[scope] ?? {
              title: scope,
              description: 'Permission non documentée — refusez en cas de doute.',
            };
            return (
              <li
                key={scope}
                className={`rounded-lg border p-3 ${
                  info.sensitive
                    ? 'border-danger-border bg-danger-soft'
                    : 'border-border bg-surface'
                }`}
              >
                <p className="font-medium text-text">{info.title}</p>
                <p className="text-sm text-text-muted mt-0.5">{info.description}</p>
                <p className="text-[10px] mt-1 text-text-subtle font-mono">{scope}</p>
              </li>
            );
          })}
        </ul>
        {ctx.requested_scopes.length !== ctx.grantable_scopes.length && (
          <p className="text-xs text-text-muted italic">
            Certains scopes demandés ne sont pas autorisés pour ce client et seront ignorés.
          </p>
        )}
      </section>

      <section className="rounded-lg bg-surface-2 p-3 text-xs text-text-muted">
        <p>
          En autorisant, vous transmettez à <strong>{ctx.client_name}</strong> un jeton d’accès
          limité à 1 heure (renouvelable). Vous pouvez révoquer cette autorisation à tout moment
          depuis <em>Compte → Connecteurs IA</em>.
        </p>
        <p className="mt-1.5">
          Aucune donnée répondant (réponses individuelles) ne sera transmise tant que la
          permission correspondante n’est pas accordée explicitement, et seulement après signature
          de l’AIPD enquête mise à jour (RGPD art. 35).
        </p>
      </section>

      {errMsg && <p className="field-error">{errMsg}</p>}

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          className="btn-secondary min-w-[120px]"
          onClick={() => decide('deny')}
          disabled={busy}
        >
          Refuser
        </button>
        <button
          type="button"
          className="btn-primary min-w-[140px]"
          onClick={() => decide('allow')}
          disabled={busy}
        >
          {busy ? 'Envoi…' : 'Autoriser'}
        </button>
      </div>
    </ConsentShell>
  );
}

function ConsentShell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <main className="min-h-screen bg-bg flex items-center justify-center p-4">
      <div className="card max-w-lg w-full space-y-5">{children}</div>
    </main>
  );
}
